import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Box, Typography, Button, IconButton, CircularProgress, FormControl, InputLabel, Select, MenuItem, Card, CardContent, Divider,  } from '@mui/material';
import {
  Close as CloseIcon,
  ContentCut as TailorIcon,
  Send as SendIcon,
  Assignment as WorkIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import client from '../api/client';
import CustomTextField from '../components/CustomTextField';
import { toPersianDigits } from '../utils/numberConverter';

const themeColors = {
  dark: '#1a1a2e',
  gold: '#d4af37',
  goldLight: '#f4e4bc',
};

const SendToTailorDialog = ({ open, onClose, order, measurements, onSuccess }) => {
  const [tailors, setTailors] = useState([]);
  const [selectedTailor, setSelectedTailor] = useState('');
  const [notes, setNotes] = useState('');
  const [workOrders, setWorkOrders] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      fetchTailors();
      // مقداردهی اولیه دستور کارها برای هر اندازه‌گیری
      const initialWorkOrders = {};
      measurements?.forEach(m => {
        initialWorkOrders[m.id] = '';
      });
      setWorkOrders(initialWorkOrders);
    }
  }, [open, measurements]);

  const fetchTailors = async () => {
    setLoading(true);
    try {
      const response = await client.get('/orders/tailors/');
      setTailors(response.data);
    } catch (error) {
      toast.error('خطا در دریافت لیست خیاط‌ها');
    } finally {
      setLoading(false);
    }
  };

  const handleWorkOrderChange = (measurementId, value) => {
    setWorkOrders(prev => ({
      ...prev,
      [measurementId]: value
    }));
  };

  const handleSubmit = async () => {
    if (!selectedTailor) {
      toast.error('لطفاً خیاط را انتخاب کنید');
      return;
    }

    // چک کردن که همه دستور کارها پر شده باشه
    const emptyWorkOrders = Object.entries(workOrders).filter(([_, v]) => !v.trim());
    if (emptyWorkOrders.length > 0) {
      toast.error('لطفاً دستور کار همه اندازه‌گیری‌ها را وارد کنید');
      return;
    }

    setSubmitting(true);
    try {
      const data = {
        tailor_id: selectedTailor,
        notes: notes,
        work_orders: Object.entries(workOrders).map(([measurementId, instructions]) => ({
          measurement_id: parseInt(measurementId),
          instructions: instructions
        }))
      };

      await client.post(`/orders/${order.id}/send-to-tailor/`, data);
      toast.success('سفارش به خیاط ارسال شد');
      onSuccess?.();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.error || 'خطا در ارسال به خیاط');
    } finally {
      setSubmitting(false);
    }
  };

  

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          bgcolor: themeColors.dark,
          color: themeColors.gold,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TailorIcon />
          <Typography variant="h6">ارسال به خیاط</Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: themeColors.gold }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress sx={{ color: themeColors.gold }} />
          </Box>
        ) : (
          <>
            {/* انتخاب خیاط */}
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>انتخاب خیاط</InputLabel>
              <Select
                value={selectedTailor}
                onChange={(e) => setSelectedTailor(e.target.value)}
                label="انتخاب خیاط"
              >
                {tailors.map(tailor => (
                  <MenuItem key={tailor.id} value={tailor.id}>
                    {tailor.full_name} - {toPersianDigits(tailor.mobile)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {tailors.length === 0 && (
              <Typography color="error" sx={{ mb: 2, textAlign: 'center' }}>
                هنوز خیاطی ثبت نشده. ابتدا از منوی خیاط‌ها، یک خیاط اضافه کنید.
              </Typography>
            )}

            {/* توضیحات کلی */}
            <CustomTextField               fullWidth
              label="توضیحات کلی (اختیاری)"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              multiline
              rows={2}
              sx={{ mb: 3 }}
            />

            <Divider sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                دستور کار هر اندازه‌گیری
              </Typography>
            </Divider>

            {/* دستور کار برای هر اندازه‌گیری */}
            {measurements?.map((measurement, index) => (
              <Card key={measurement.id} sx={{ mb: 2, bgcolor: 'grey.50' }}>
                <CardContent sx={{ pb: 2, '&:last-child': { pb: 2 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <WorkIcon sx={{ color: themeColors.gold, fontSize: 20 }} />
                    <Typography variant="subtitle2" fontWeight="bold">
                      {toPersianDigits((index + 1).toString())}. {measurement.location}
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    ارتفاع: {toPersianDigits(measurement.height)} cm × عرض: {toPersianDigits(measurement.width)} cm
                    {measurement.fabric_types_names?.length > 0 && (
                      <> | پارچه: {measurement.fabric_types_names.join('، ')}</>
                    )}
                  </Typography>
                  <CustomTextField                     fullWidth
                    label="دستور کار *"
                    value={workOrders[measurement.id] || ''}
                    onChange={(e) => handleWorkOrderChange(measurement.id, e.target.value)}
                    multiline
                    rows={2}
                    placeholder="دستور کار برای خیاط..."
                    size="small"
                  />
                </CardContent>
              </Card>
            ))}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose}>انصراف</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting || tailors.length === 0}
          startIcon={submitting ? <CircularProgress size={20} /> : <SendIcon />}
          sx={{
            bgcolor: themeColors.gold,
            color: themeColors.dark,
            '&:hover': { bgcolor: themeColors.goldLight },
          }}
        >
          ارسال به خیاط
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SendToTailorDialog;